import { getBFFApiClient } from '@acp/feature/auth';
import {
    APIResponse,
    Document,
    PropertyDocument,
    PropertyDocumentService,
    PropertyDocumentType,
    UpdatePropertyDocument,
} from '@acp/shared';
import HttpStatusCodes from 'http-status-codes';

async function getPropertyDocuments(
    propertyId: number,
    filters: string[],
    facets: string[]
) {
    const response = await getBFFApiClient().get<APIResponse<{
        totalCount: number;
        resultsCount: number;
        items: Document[];
    }>>(`/v1/property/${propertyId}/documents`, {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        params: {
            filters,
            facets,
        },
    });

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Property documents not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this property');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get property documents');
    }

    return response.data.data;
}

async function deletePropertyDocument(
    propertyId: number,
    documentId: number
) {
    const response = await getBFFApiClient().delete<APIResponse<null>>(
        `/v1/property/${propertyId}/documents/${documentId}`
    );

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Property document not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this document');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to delete property document');
    }
}

async function deleteIntegratedPropertyDocument(
    agencyPortfolioPropertyId: number,
    documentId: number
) {
    const response = await getBFFApiClient().delete<APIResponse<null>>(
        `/v1/integrated-property/${agencyPortfolioPropertyId}/documents/${documentId}`
    );

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Integrated property document not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this document');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to delete integrated property document');
    }
}

async function getAllPropertyDocumentTypes() {
    const response = await getBFFApiClient().get<APIResponse<PropertyDocumentType[]>>('/v1/meta/property-document-types');

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get document types');
    }

    return response.data.data;
}

async function getAllPropertyDocumentServices() {
    const response = await getBFFApiClient().get<APIResponse<PropertyDocumentService[]>>('/v1/meta/property-document-services');

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get document services');
    }

    return response.data.data;
}

async function updatePropertyDocument(
    propertyId: number,
    documentId: number,
    values: UpdatePropertyDocument
) {
    const response = await getBFFApiClient().patch<APIResponse<PropertyDocument>>(
        `/v1/property/${propertyId}/documents/${documentId}`,
        values
    );

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Property document not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this document');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to update property document');
    }

    return response.data.data;
}

async function updateIntegratedPropertyDocument(
    agencyPortfolioPropertyId: number,
    documentId: number,
    values: UpdatePropertyDocument
) {
    const response = await getBFFApiClient().patch<APIResponse<PropertyDocument>>(
        `/v1/integrated-property/${agencyPortfolioPropertyId}/documents/${documentId}`,
        values
    );

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Integrated roperty document not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this document');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to update integrated property document');
    }

    return response.data.data;
}

async function getIntegratedPropertyDocuments(
    agencyPortfolioPropertyId: number,
    filters: string[],
    facets: string[]
) {
    const response = await getBFFApiClient().get<APIResponse<{
        totalCount: number;
        resultsCount: number;
        items: Document[];
    }>>(`/v1/integrated-property/${agencyPortfolioPropertyId}/documents`, {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        params: {
            filters,
            facets,
        },
    });

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Integrated property documents not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this integrated property');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get integrated property documents');
    }

    return response.data.data;
}

export {
    getPropertyDocuments,
    deletePropertyDocument,
    getAllPropertyDocumentTypes,
    updatePropertyDocument,
    getAllPropertyDocumentServices,
    getIntegratedPropertyDocuments,
    updateIntegratedPropertyDocument,
    deleteIntegratedPropertyDocument,
};
